import React, { useMemo } from 'react';
import { View, Text, StyleSheet, Platform, Pressable, type TextStyle } from 'react-native';
import { appTheme, type ThemeColors } from '../../shared/theme';
import { hexToRgba } from '../../shared/theme/colorUtils';

const ANDROID_TEXT: TextStyle =
  Platform.OS === 'android' ? { includeFontPadding: false, textAlignVertical: 'center' } : {};

export type ProfileGenderValue = 'male' | 'female';

type ProfileGenderToggleProps = {
  colors: ThemeColors;
  value: ProfileGenderValue;
  onChange: (value: ProfileGenderValue) => void;
  male_label: string;
  female_label: string;
  disabled?: boolean;
  border_radius?: number;
};

function normalizeValue(value: string): ProfileGenderValue {
  return value === 'female' ? 'female' : 'male';
}

export function ProfileGenderToggle({
  colors,
  value,
  onChange,
  male_label,
  female_label,
  disabled = false,
  border_radius = appTheme.borderRadius.md,
}: ProfileGenderToggleProps) {
  const selected = normalizeValue(value);
  const styles = useMemo(() => createStyles(colors, border_radius), [colors, border_radius]);

  const render_option = (option: ProfileGenderValue, label: string) => {
    const active = selected === option;
    return (
      <Pressable
        key={option}
        onPress={() => {
          if (disabled) {
            return;
          }
          onChange(option);
        }}
        disabled={disabled}
        accessibilityRole="button"
        accessibilityState={{ selected: active, disabled }}
        style={({ pressed }) => [
          styles.option,
          active && styles.option_active,
          disabled && styles.option_disabled,
          pressed && !disabled && styles.option_pressed,
        ]}>
        <Text style={[styles.option_text, active && styles.option_text_active]} numberOfLines={1}>
          {label}
        </Text>
      </Pressable>
    );
  };

  return (
    <View style={[styles.row, disabled && styles.row_disabled]}>
      {render_option('male', male_label)}
      {render_option('female', female_label)}
    </View>
  );
}

function createStyles(c: ThemeColors, border_radius: number) {
  return StyleSheet.create({
    row: {
      flexDirection: 'row',
      alignSelf: 'stretch',
      gap: appTheme.spacing.sm,
    },
    row_disabled: {
      opacity: 0.55,
    },
    option: {
      flex: 1,
      minWidth: 0,
      minHeight: 48,
      alignItems: 'center',
      justifyContent: 'center',
      paddingVertical: appTheme.spacing.md,
      paddingHorizontal: appTheme.spacing.lg,
      borderRadius: border_radius,
      borderWidth: StyleSheet.hairlineWidth,
      borderColor: hexToRgba(c.border, 0.65),
      backgroundColor: c.surfaceLight ?? hexToRgba(c.text, 0.06),
    },
    option_active: {
      backgroundColor: c.primary,
      borderColor: c.primary,
    },
    option_disabled: {},
    option_pressed: {
      opacity: 0.92,
    },
    option_text: {
      ...appTheme.typography.bodyMedium,
      color: c.textSecondary,
      fontWeight: '600',
      textAlign: 'center',
      width: '100%',
      ...ANDROID_TEXT,
    },
    option_text_active: {
      color: c.onPrimary,
    },
  });
}
